export type AdviceJobLifecycle =
  | 'Created'
  | 'Dispatching'
  | 'AwaitingReaction'
  | 'AwaitingResponse'
  | 'Booked'
  | 'Expired'
  | 'Failed'
  | 'Superseded';

// PLAN-0034: AwaitingReaction (dispatched, consignee not yet engaged) is a bookable pre-booking
// state — the offer GET can surface it on the first open before the form-open advances the job.
export const PRE_BOOKING_STATUSES: ReadonlyArray<AdviceJobLifecycle> = [
  'Created',
  'Dispatching',
  'AwaitingReaction',
  'AwaitingResponse',
];

export const TERMINAL_UNHAPPY_STATUSES: ReadonlyArray<AdviceJobLifecycle> = [
  'Expired',
  'Failed',
  'Superseded',
];

export type ConsumerDeliveryDate = {
  id: string;
  dateValue: string;            // ISO date (YYYY-MM-DD)
  windowStart: string | null;   // ISO datetime
  windowEnd: string | null;
  bookableUntil: string;        // ISO datetime
  displayLabel: string | null;
};

export type ConsumerHint = {
  code: string;
  label: string;
};

export type ConsumerExtraService = {
  id: string;
  label: string;
  description: string | null;
};

export type ConsumerDropOffLocation = {
  id: string;
  label: string;
};

export type ConsumerDropOff = {
  locationOptions: ConsumerDropOffLocation[];
  termsBody: string;
  termsVersion: string;
};

export type TenantBranding = {
  logoUrl: string | null;
  primaryColor: string | null;
};

export type FeedbackMode = 'Global' | 'Tenant' | 'ChooseInFeedbackForm' | 'Off';
export type FeedbackCategory = 'technical' | 'business';

export type ConsumerOffer = {
  adviceJobId: string;
  status: AdviceJobLifecycle;
  languagePreference: string;
  deliveryDates: ConsumerDeliveryDate[];
  hints: ConsumerHint[];
  extraServicesOffered: ConsumerExtraService[];
  branding?: TenantBranding | null;
  imprintHtml?: string | null;
  // ISSUE-0039: effective consumer feedback mode for this tenant.
  feedbackMode?: FeedbackMode;
  // ISSUE-0082: resolved cookie-consent banner text for this tenant + language.
  cookieBannerText?: string | null;
  // Abstellgenehmigung: present only when the shipment activated the option.
  dropOffAuthorization?: ConsumerDropOff | null;
  // ISSUE-0126: shipment info shown read-only on the booking form (number + origin).
  externalShipmentId?: string | null;
  senderName?: string | null;
  // ISSUE-0137: multi-line address blocks for the Sendungsinfo section (Absender / Empfänger).
  senderAddressDisplay?: string | null;
  consigneeAddressDisplay?: string | null;
  // ISSUE-0195: order positions from AdditionalData, pre-joined by the server with `\n`.
  shipmentDetails?: string | null;
};

export type FeedbackSubmitRequest = {
  subject: string;
  description: string;
  category?: FeedbackCategory | null;
  context?: unknown;
};

export type LegalContent = {
  slug: string;
  language: string;
  title: string;
  bodyHtml: string;
  bodyText: string | null;
  updatedAt: string;
};

export type LegalSlug = 'impressum' | 'datenschutz' | 'terms' | 'data-deletion';

export type ConsumerDropOffSelection = {
  locationOptionId: string | null;
  note: string | null;
  termsAccepted: boolean;
};

export type ConsumerSelectRequest = {
  deliveryDateId: string | null;
  selectedExtraIds: string[] | null;
  dropOffAuthorization?: ConsumerDropOffSelection | null;
};

export type ConsumerSelectResponse = {
  adviceJobId: string;
  status: 'Booked';
  selectedDeliveryDateId: string | null;
  bookedAt: string;             // ISO datetime
};

export type ProblemDetails = {
  code: string;
  title: string;
  status: number;
  detail?: string;
  correlationId?: string;
  /** Short quotable UID for a persisted server fault (5xx). Format E-XXXXXXXX. */
  errorId?: string;
};

export type TerminalTokenErrorCode =
  | 'invalid_token_format'
  | 'invalid_signature'
  | 'expired'
  | 'job_not_found';

export type PostSelectErrorCode =
  | 'already_booked'
  | 'invalid_transition'
  | 'date_no_longer_bookable'
  | 'unknown_delivery_date'
  | 'unknown_extra_id'
  | 'ambiguous_booking_choice'
  | 'no_booking_choice'
  | 'terms_not_accepted'
  | 'dropoff_not_enabled'
  | 'dropoff_note_too_long'
  | 'unknown_dropoff_location';
